import { ColdIcon, WarmIcon, HotIcon, RemittanceIcon, InsuranceIcon, FinancingIcon, ReferralIcon, ReKYCIcon } from "../../lead-inventory/icons";

import AssignedTableActions from "./accAssignedTableAction";
import AssignFarmerModal from "./AssignFarmerModal";
import SidebarTabsContents from "../../account-inventory/all/sidebar-components/accountSidebar.jsx";
import AccountCard from "../../account-inventory/AccountCard";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { toast } from "react-toastify";
import { apiRequest } from "@/network/apiClient";
import { useIsMobile } from "@/hooks";

const AccountInventoryTable = ({ data = [], onSelectionChange, clearOnSome = true, farmers = [], onRefresh }) => {
  const isMobile = useIsMobile();
  const [showSidebar, setShowSidebar] = useState(false);
  const [leadID, setLeadID] = useState(null);
  const [churnLikelihood, setChurnLikelihood] = useState(null);
  const [profileName, setProfileName] = useState(null);
  const [relationship, setRelationship] = useState(null);
  const [leadName, setLeadName] = useState(null);
  const [hunterName, setHunterName] = useState(null);
  const [isUpdatingFarmerId, setIsUpdatingFarmerId] = useState(false);
  const [selectedFarmerId, setSelectedFarmerId] = useState("");
  const [showFarmerSelector, setShowFarmerSelector] = useState(false);

  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [assignLeadId, setAssignLeadId] = useState(null);
  const [assignAccountName, setAssignAccountName] = useState("");
  const [isAssignSubmitting, setIsAssignSubmitting] = useState(false);

  // ===== Selection state (Gmail-style) =====
  const [selected, setSelected] = useState(() => new Set());
  const lastSelectedIndex = useRef(null);
  const headerCheckboxRef = useRef(null);

  const allIds = useMemo(() => data.map((d) => d.lead_id), [data]);
  const total = data.length;
  const selectedCount = selected.size;
  const allSelected = total > 0 && selectedCount === total;
  const someSelected = selectedCount > 0 && selectedCount < total;

  const openAssignModal = (leadId) => {
    const row = data.find((d) => d.lead_id === leadId);
    setAssignLeadId(leadId);
    setAssignAccountName(row?.name || "");
    setIsAssignOpen(true);
  };

  const handleAssignFarmerSingle = async (farmerId) => {
    try {
      setIsAssignSubmitting(true);
      await updateLeadFarmerId(assignLeadId, farmerId);
      setIsAssignOpen(false);
      
      // Use the refresh function from parent instead of window.location.reload()
      if (onRefresh) {
        await onRefresh();
      }
    } catch (e) {
      toast.error(e.message || 'Failed to assign farmer');
    } finally {
      setIsAssignSubmitting(false);
    }
  };

  // Expose selection upwards if needed
  useEffect(() => {
    onSelectionChange?.(Array.from(selected));
  }, [selected, onSelectionChange]);

  // Keep header checkbox indeterminate state in sync
  useEffect(() => {
    if (headerCheckboxRef.current) headerCheckboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  // Function to update farmer_id for a single lead
  const updateLeadFarmerId = async (leadId, farmerId) => {
    try {
      const result = await apiRequest(`${import.meta.env.VITE_BASE_URL}/leads/${leadId}/farmer_id`, 'PUT', { farmer_id: farmerId })

      if (!result) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update farmer_id');
      }

      return await result
    } catch (error) {
      console.error(`Error updating farmer_id for lead ${leadId}:`, error);
      throw error;
    }
  };

  // Function to update farmer_id for all selected leads
  const updateSelectedLeadsFarmerId = async (selectedLeadIds, farmerId) => {
    if (!farmerId || selectedLeadIds.length === 0) {
      return;
    }

    setIsUpdatingFarmerId(true);
    const results = {
      successful: [],
      failed: [],
    };

    try {
      // Process updates in parallel with a reasonable concurrency limit
      const batchSize = 5;
      for (let i = 0; i < selectedLeadIds.length; i += batchSize) {
        const batch = selectedLeadIds.slice(i, i + batchSize);
        const batchPromises = batch.map(async (leadId) => {
          try {
            const result = await updateLeadFarmerId(leadId, farmerId);
            results.successful.push({ leadId, result });
          } catch (error) {
            results.failed.push({ leadId, error: error.message });
          }
        });

        await Promise.all(batchPromises);
      }

      // Log results
      console.log(`Successfully updated ${results.successful.length} accounts`);
      if (results.failed.length > 0) {
        console.error(`Failed to update ${results.failed.length} accounts:`, results.failed);
      }

      // Show success/error message to user
      if (results.successful.length > 0) {
        const successMsg = `Successfully assigned farmer to ${results.successful.length} account${results.successful.length > 1 ? 's' : ''}`;
        const failMsg = results.failed.length > 0 ? `. Failed to update ${results.failed.length} account${results.failed.length > 1 ? 's' : ''}.` : '';
        toast.success(successMsg + failMsg);
      }

    } catch (error) {
      console.error('Error in batch update:', error);
      toast.error('An error occurred during batch update');
    } finally {
      setIsUpdatingFarmerId(false);
    }

    return results;
  };

  const emitSelection = useCallback((newSet) => setSelected(newSet), []);

  // Function to manually assign farmer to selected accounts
  const handleAssignFarmer = async () => {
    if (!selectedFarmerId || selected.size === 0) {
      toast.info('Please select accounts and choose a farmer to assign');
      return;
    }

    const selectedLeadIds = Array.from(selected);
    await updateSelectedLeadsFarmerId(selectedLeadIds, parseInt(selectedFarmerId, 10));

    // Use the refresh function from parent instead of window.location.reload()
    if (onRefresh) {
      await onRefresh();
    }
    
    // Clear selection after successful assignment
    emitSelection(new Set());
    setShowFarmerSelector(false);
    setSelectedFarmerId("");
  };

  const updateSelection = useCallback(
    (ids, shouldSelect) => {
      const next = new Set(selected);
      ids.forEach((id) => (shouldSelect ? next.add(id) : next.delete(id)));
      emitSelection(next);
    },
    [selected, emitSelection]
  );

  const handleSelectAll = useCallback(() => {
    let next;

    if (clearOnSome) {
      // If anything is selected -> clear all, else select all
      if (selectedCount === 0) {
        next = new Set(allIds);
      } else {
        next = new Set();
      }
    } else {
      // Toggle strictly by "all selected"
      if (allSelected) {
        next = new Set();
      } else {
        next = new Set(allIds);
      }
    }

    emitSelection(next);
    lastSelectedIndex.current = null;
  }, [allIds, allSelected, clearOnSome, emitSelection, selectedCount]);

  const handleRowToggle = useCallback(
    (index, event) => {
      // event can be from checkbox or row click
      const row = data[index];
      if (!row) return;

      const id = row.lead_id;
      const currentlySelected = selected.has(id);
      const shouldSelect = !currentlySelected;

      if (event?.shiftKey && lastSelectedIndex.current !== null && lastSelectedIndex.current !== index) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        const rangeIds = data.slice(start, end + 1).map((r) => r.lead_id);
        updateSelection(rangeIds, shouldSelect);
      } else {
        updateSelection([id], shouldSelect);
      }

      lastSelectedIndex.current = index;
    },
    [data, selected, updateSelection]
  );

  const handleDetailClick = (leadID) => {
    setShowSidebar(true);
    setLeadID(leadID);
    setChurnLikelihood(data.find((item) => item.lead_id === leadID)?.churn_profile);
    setProfileName(data.find((item) => item.lead_id === leadID)?.profile_name);
    setRelationship(data.find((item) => item.lead_id === leadID)?.relationship);
    setHunterName(data.find((item) => item.lead_id === leadID)?.hunter_username);
    setLeadName(data.find((item) => item.lead_id === leadID)?.name);
  };

  // Prevent row click when clicking checkbox / actions
  const isInteractive = (el) => {
    if (!el) return false;
    const tag = el.tagName?.toLowerCase();
    return (
      tag === "input" ||
      tag === "button" ||
      tag === "svg" ||
      tag === "path" ||
      el.closest("button") ||
      el.closest("input")
    );
  };

  const getProfileStyle = (status) => {
    const styles = {
        'Fresh Meat': "bg-[#FFDAD4] border-[#FFDAD4] text-black",
        Dormant: "bg-[#DFE0FF] border-[#DFE0FF] text-black",
        Newbie: "bg-[#FFDAD4] border-[#FFDAD4] text-black",
        'Farmable (Avg.)': "bg-[#FFEDE9] border-[#FFEDE9] text-black",
        'Growth Potential': "bg-[#FFEDE9] border-[#FFEDE9] text-black",
        'Core Customer (Loyal)': "bg-[#FFEDE9] border-[#FFEDE9] text-black",
        Losers: "bg-[#DFE0FF] border-[#DFE0FF] text-black",
        'Farmable (Loose)': "bg-[#FFDAD4] border-[#FFDAD4] text-black",
        'Bargain Hunter': "bg-[#FFDAD4] border-[#FFDAD4] text-black",
        'Need-Based (Avg.)': "bg-[#FFEDE9] border-[#FFEDE9] text-black",
        'Farmable (Tight)': "bg-[#FFEEDC] border-[#FFEEDC] text-black",
        'Need-Based (Loose)': "bg-[#FFDAD4] border-[#FFDAD4] text-black",
        Wanderer: "bg-[#DFE0FF] border-[#DFE0FF] text-black",
        'Need-Based (Tight)': "bg-[#FFDAD4] border-[#FFDAD4] text-black",
        'Core Customer (Ex-GF)': "bg-[#DFE0FF] border-[#DFE0FF] text-black"
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  const getChurnStatusStyle = (status) => {
    const styles = {
        Activated: "bg-[#F5FAE5] border-[#F5FAE5] text-black",
        Dormant: "bg-[#FFF0F5] border-[#FFF0F5] text-black",
        Sabik: "bg-[#EBF8FF] border-[#EBF8FF] text-black",
        Sirius: "bg-[#FEFAD8] border-[#FEFAD8] text-black",
        Spica: "bg-[#FBF29D] border-[#FBF29D] text-black",
        Sargas: "bg-[#FFDDB6] border-[#FFDDB6] text-black"
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  return (
    <>
      {/* Selection summary with farmer assignment controls - Desktop only */}
      {!isMobile && selectedCount > 0 && (
        <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-blue-900">
                {selectedCount} account{selectedCount > 1 ? 's' : ''} selected
              </span>
              
              {isUpdatingFarmerId && (
                <span className="text-blue-600 text-sm">
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full mr-1"></span>
                  Updating farmer IDs...
                </span>
              )}
            </div>

            <div className="flex items-center space-x-3">
              {!showFarmerSelector ? (
                <button
                  onClick={() => setShowFarmerSelector(true)}
                  className="px-4 py-2 bg-yellow-200 text-black text-sm border-2 border-black-600 rounded hover:bg-yellow-300 transition-colors"
                  disabled={isUpdatingFarmerId}
                >
                  Assign Farmer
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <select
                    value={selectedFarmerId}
                    onChange={(e) => setSelectedFarmerId(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isUpdatingFarmerId}
                  >
                    <option value="">Select Farmer</option>
                    {farmers.map((farmer) => (
                      <option key={farmer.id} value={farmer.id}>
                        {farmer.username}
                      </option>
                    ))}
                  </select>
                  
                  <button
                    onClick={handleAssignFarmer}
                    disabled={!selectedFarmerId || isUpdatingFarmerId}
                    className="px-3 py-1 bg-yellow-200 text-black text-sm rounded hover:bg-yellow-300 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    Assign
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowFarmerSelector(false);
                      setSelectedFarmerId("");
                    }}
                    className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600 transition-colors"
                    disabled={isUpdatingFarmerId}
                  >
                    Cancel
                  </button>
                </div>
              )}

              <button
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                onClick={() => {
                  emitSelection(new Set());
                  lastSelectedIndex.current = null;
                  setShowFarmerSelector(false);
                  setSelectedFarmerId("");
                }}
                disabled={isUpdatingFarmerId}
              >
                Clear Selection
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="">
        {/* Mobile: Card View */}
        {isMobile ? (
          <div className="space-y-3">
            {data.length > 0 ? (
              data.map((item, index) => (
                <AccountCard
                  key={`card-${item.lead_id}-${index}`}
                  data={item}
                  onClick={() => handleDetailClick(item.lead_id)}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No accounts found
              </div>
            )}
          </div>
        ) : (
          /* Desktop: Table View */
          <table className="min-w-full bg-white border-b border-gray-200 table-fixed">
          <thead key="thead">
            <tr key="header-row" className="text-gray-400 border-t">
              {/* NEW: select-all checkbox column */}
              <th className="p-4 border-b text-center w-[4%]">
                <input
                  ref={headerCheckboxRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  aria-checked={allSelected ? "true" : someSelected ? "mixed" : "false"}
                  className="h-4 w-4"
                  disabled={isUpdatingFarmerId}
                />
              </th>
              <th key="header-id" className="p-4 border-b text-left w-[5%]">ID</th>
              <th key="header-product" className="p-4 border-b text-left w-[5%]">Product</th>
              <th key="header-name" className="p-4 border-b text-left w-[25%]">Name</th>
              <th key="header-status" className="p-4 border-b text-left w-[10%]">Churn Likelihood</th>
              <th key="header-pipeline" className="p-4 border-b text-left w-[20%]">Profile Name</th>
              <th key="header-source" className="p-4 border-b text-left w-[15%]">Last Transacted</th>
              <th key="header-interaction" className="p-4 border-b text-left w-[15%]">Last Interacted</th>
              <th key="header-owner" className="p-4 border-b text-left w-[3%]">Farmer</th>
              <th key="header-action" className="p-4 border-b text-left w-[2%]">Action</th>
            </tr>
          </thead>
          <tbody key="tbody">
            {data.length > 0 ? (
              data.map((item, index) => {
                const isSelected = selected.has(item.lead_id);
                return (
                  <tr 
                    key={`row-${item.lead_id}-${index}`} 
                    className={`hover:bg-[#F7F7F7] ${isSelected ? "bg-blue-50" : ""}`}
                    onClick={(e) => {
                      if (isInteractive(e.target)) return;
                      handleDetailClick(item.lead_id);
                    }}
                  >
                    {/* NEW: row checkbox */}
                    <td className="p-4 border-b text-center w-[4%]" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleRowToggle(index, e.nativeEvent || e);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        disabled={isUpdatingFarmerId}
                      />
                    </td>
                    <td key={`id-${item.lead_id}`} className="p-4 border-b text-gray-500 text-left w-[5%]">{item.lead_id}</td>
                    <td key={`product-${item.lead_id}`} className="p-4 border-b text-left w-[5%]">
                      {item.products.includes('Remittance') && (
                        <img key={`remittance-${item.lead_id}`} src={RemittanceIcon} alt="Remittance" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products.includes('Insurance') && (
                        <img key={`insurance-${item.lead_id}`} src={InsuranceIcon} alt="Insurance" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products.includes('Financing') && (
                        <img key={`financing-${item.lead_id}`} src={FinancingIcon} alt="Financing" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products.includes('Referral') && (
                        <img key={`referral-${item.lead_id}`} src={ReferralIcon} alt="Referral" className="inline-block w-6 h-6 mr-2" />
                      )}
                      {item.products.includes('Re-KYC') && (
                        <img key={`rekyc-${item.lead_id}`} src={ReKYCIcon} alt="ReKYC" className="inline-block w-6 h-6 mr-2" />
                      )}
                    </td>
                    <td key={`name-${item.lead_id}`} className="p-4 border-b font-bold text-left w-[28%]">{item.name}</td>
                    <td key={`status-${item.lead_id}`} className="p-4 border-b text-left w-[10%]">
                      <span className={`w-[95px] h-[34px] pt-2 px-4 pb-2 gap-2 top-5 left-[22px] rounded-full border font-bold ${getChurnStatusStyle(item.churn_profile)}`}>
                        {item.churn_profile}
                      </span>
                    </td>
                    <td key={`pipeline-${item.lead_id}`} className="p-4 border-b text-left w-[20%]">
                      <span className={`w-[95px] h-[34px] pt-2 px-4 pb-2 gap-2 top-5 left-[22px] rounded-full border font-bold ${getProfileStyle(item.profile)}`}>
                        {item.profile || "Unknown"}
                      </span>
                    </td>
                    <td key={`interaction-${item.lead_id}`} className="p-4 border-b text-left w-[15%] font-bold">
                      {item.last_transaction_datetime}
                      <span key={`last-interaction-${item.lead_id}`} className="text-xs text-[#A96900] border-[#FFEEDC] bg-[#FFEEDC] w-2 h-2 p-1 ml-2 rounded-full border">
                        {item.days_from_last_transaction}
                      </span>
                    </td>
                    <td key={`interaction2-${item.lead_id}`} className="p-4 border-b text-left w-[15%] font-bold">
                      {item.updated_time}
                      <span key={`last-interaction2-${item.lead_id}`} className="text-xs text-[#A96900] border-[#FFEEDC] bg-[#FFEEDC] w-2 h-2 p-1 ml-2 rounded-full border">
                        {item.last_interacted}
                      </span>
                    </td>
                    <td key={`owner-${item.lead_id}`} className="p-4 border-b text-left w-[3%] font-bold">
                      <div className="relative group inline-block">
                        <span className="bg-[#EEEEEE] w-10 h-10 py-2 px-3 rounded-full inline-flex items-center justify-center">
                          {item.farmer_username ? item.farmer_username[0].toUpperCase() : ''}
                        </span>
                        <div className="absolute hidden group-hover:block bg-gray-800 text-white text-sm rounded py-1 px-2 -bottom-8 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
                          {item.farmer_username ? item.farmer_username[0].toUpperCase() + item.farmer_username.slice(1) : 'No Owner'}
                        </div>
                      </div>
                    </td>
                    <td key={`action-${item.lead_id}`} className="p-4 border-b text-center w-[2%]"
                      onClick={(e) => e.stopPropagation()}>
                      <AssignedTableActions leadId={item.lead_id} onAssignFarmer={openAssignModal} />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr key="no-data-row">
                <td key="no-data-cell" colSpan="10" className="text-center py-4 text-gray-500 border-b">
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        )}
      </div>

      <AssignFarmerModal
        open={isAssignOpen}
        accountName={assignAccountName}
        farmers={farmers}
        onClose={() => setIsAssignOpen(false)}
        onSubmit={handleAssignFarmerSingle}
        isSubmitting={isAssignSubmitting}
      />
      
      <SidebarTabsContents
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        leadID={leadID}
        churnLikelihood={churnLikelihood}
        profileName={profileName}
        relationship={relationship}
        leadName={leadName}
        hunterName={hunterName}
      />
    </>
  );
};

export default AccountInventoryTable;